import React, { useEffect, useRef, useState } from 'react';
import { designThumbnailKey, isDirectMediaSrc, peekCachedBlobUrl, requestAuthenticatedBlobUrl } from '../../api/mediaUrl';

/** Start loading lazy images a little before they scroll into view. */
const LAZY_ROOT_MARGIN = '300px 0px';

/**
 * Nearest scrolling ancestor (e.g. the app's main content panel or a dropdown list).
 * Observing against it lets `LAZY_ROOT_MARGIN` preload images just below its visible area.
 */
function findScrollParent(el) {
  let node = el ? el.parentElement : null;
  while (node && node !== document.body && node !== document.documentElement) {
    const { overflowY } = window.getComputedStyle(node);
    if (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') return node;
    node = node.parentElement;
  }
  return null; // the browser viewport
}

function initialDisplaySrc(src) {
  const trimmed = String(src || '').trim();
  if (!trimmed) return '';
  if (isDirectMediaSrc(trimmed)) return trimmed;
  return peekCachedBlobUrl(trimmed);
}

/**
 * Renders images stored on the private file API (designs, profiles, order docs).
 * Legacy base64 data URIs and external URLs are shown directly.
 *
 * - `loading="lazy"`: the file is downloaded only when the image is near the visible area
 *   (use in long lists / grids). Until then an empty placeholder with the same `className`
 *   keeps the layout.
 * - `priority="high"`: download before queued list images (use for previews the user opened).
 * - `thumbnail`: show the small list copy of a design image (falls back to the original if the
 *   thumbnail cannot be loaded). Use only where the image is shown small.
 */
export function AuthenticatedImage({
  src,
  alt = '',
  className,
  fallback = null,
  priority = 'normal',
  thumbnail = false,
  ...props
}) {
  const lazy = props.loading === 'lazy';
  const originalSrc = String(src || '').trim();
  const thumbSrc = thumbnail ? designThumbnailKey(originalSrc) : '';
  /** Set when the thumbnail failed: use the original image instead. */
  const [thumbFailedFor, setThumbFailedFor] = useState('');
  const useThumb = Boolean(thumbSrc) && thumbFailedFor !== originalSrc;
  const trimmed = useThumb ? thumbSrc : originalSrc;
  const [displaySrc, setDisplaySrc] = useState(() => initialDisplaySrc(trimmed));
  const [failed, setFailed] = useState(false);
  /** Lazy mode: true while the placeholder is on (or near) the screen. */
  const [nearView, setNearView] = useState(() => !lazy);
  const placeholderRef = useRef(null);

  // Lazy mode: follow the placeholder in and out of view until the image has loaded, so
  // images the user scrolled past are taken out of the download queue again.
  useEffect(() => {
    if (!lazy || displaySrc) return undefined;
    if (typeof IntersectionObserver === 'undefined') {
      setNearView(true);
      return undefined;
    }
    const el = placeholderRef.current;
    if (!el) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        const latest = entries[entries.length - 1];
        if (latest) setNearView(latest.isIntersecting);
      },
      { root: findScrollParent(el), rootMargin: LAZY_ROOT_MARGIN }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [lazy, displaySrc]);

  useEffect(() => {
    setFailed(false);

    if (!trimmed) {
      setDisplaySrc('');
      return undefined;
    }
    if (isDirectMediaSrc(trimmed)) {
      setDisplaySrc(trimmed);
      return undefined;
    }
    const cached = peekCachedBlobUrl(trimmed);
    if (cached) {
      setDisplaySrc(cached);
      return undefined;
    }
    if (!nearView) {
      return undefined;
    }

    let cancelled = false;
    const { promise, cancel } = requestAuthenticatedBlobUrl(trimmed, { priority });
    promise
      .then((blobUrl) => {
        if (!cancelled) {
          setDisplaySrc(blobUrl);
        }
      })
      .catch(() => {
        if (cancelled) return;
        if (useThumb) {
          // Thumbnail unavailable (e.g. during a deploy): try the original image once.
          setThumbFailedFor(originalSrc);
          return;
        }
        setDisplaySrc('');
        setFailed(true);
      });

    return () => {
      cancelled = true;
      cancel();
    };
  }, [trimmed, nearView, priority, useThumb, originalSrc]);

  if (!displaySrc) {
    if (lazy && trimmed && !failed) {
      return <span ref={placeholderRef} className={className} style={{ display: 'block' }} aria-hidden="true" />;
    }
    return fallback;
  }

  return <img src={displaySrc} alt={alt} className={className} {...props} />;
}
